<script setup lang="ts">
// Demand Trend — port of v3 `ProductInsightsTopProducts.vue` (231 ln) onto 02's DataTable +
// Pagination (ORP-18953, contract 27 §10). The hand-rolled 4-button pager dies with its D2
// defect (`pageCount` was never assigned, so page 2+ was unreachable); filter/sort/page live
// in `useDemandTrend`, whose headless view carries the D2/D3 regression specs.
import { computed } from 'vue'

import {
  DEMAND_TREND_PAGE_SIZE,
  useDemandTrend,
  type DemandChangeFilter,
} from '../../composables/useDemandTrend'
import type { DemandTrendRow } from '../../models/productInsights.types'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import {
  Button,
  DataTable,
  Icon,
  Pagination,
  Select,
  Tooltip,
  type PaginationChange,
  type SelectOption,
  type SortState,
  type TableColumn,
  type UiAsyncState,
} from '@/shared/ui'

const ALL = 'ALL'

const { t } = useI18n()
const format = useFormat()
const trend = useDemandTrend()

const changeTypeOptions = computed<SelectOption<string>[]>(() => [
  { value: ALL, label: t('commerceAnalytics.productInsights.demandTrend.all') },
  { value: 'RISER', label: t('commerceAnalytics.productInsights.demandTrend.rising') },
  { value: 'SINKER', label: t('commerceAnalytics.productInsights.demandTrend.falling') },
])

const changeType = computed<string>({
  get: () => trend.changeType.value ?? ALL,
  set: (value) => {
    trend.changeType.value = value === ALL ? null : (value as DemandChangeFilter)
  },
})

const columns = computed<TableColumn<DemandTrendRow>[]>(() => [
  {
    field: 'product_name',
    header: t('commerceAnalytics.productInsights.demandTrend.productName'),
    sortable: true,
  },
  {
    field: 'product_rank',
    header: t('commerceAnalytics.productInsights.demandTrend.rank'),
    sortable: true,
    numeric: true,
  },
  {
    field: 'relative_demand',
    header: t('commerceAnalytics.productInsights.demandTrend.relativeDemand'),
    sortable: true,
  },
])

const sort = computed<SortState>(() => ({
  field: trend.sortField.value,
  dir: trend.sortOrder.value === -1 ? 'desc' : 'asc',
}))

function onSort(next: SortState): void {
  if (!next) return
  trend.sortField.value = next.field
  trend.sortOrder.value = next.dir === 'desc' ? -1 : 1
}

function onPage(change: PaginationChange): void {
  trend.page.value = Math.floor(change.first / DEMAND_TREND_PAGE_SIZE) + 1
}

const tableState = computed<UiAsyncState>(() =>
  trend.state.value === 'idle' ? 'empty' : trend.state.value,
)

function rankLabel(row: DemandTrendRow): string {
  // v3 rendered ranks through `formatFixed` — `heavyDefault` is its funnel twin.
  return format.formatNumber(typeof row.product_rank === 'number' ? row.product_rank : null, {
    heavyDefault: true,
  })
}
</script>

<template>
  <div class="demand-trend flex min-w-0 flex-col gap-md">
    <div class="demand-trend__bar flex flex-wrap items-center justify-between gap-sm">
      <h4 class="demand-trend__title m-0 text-primary text-[1.125rem] font-semibold">
        {{ t('commerceAnalytics.productInsights.demandTrend.title') }}
      </h4>
      <Select
        v-model="changeType"
        size="sm"
        :options="changeTypeOptions"
        :label="t('commerceAnalytics.productInsights.demandTrend.changeType')"
      />
    </div>

    <DataTable
      :rows="trend.rows.value"
      :columns="columns"
      v-bind="{ ariaLabel: t('commerceAnalytics.productInsights.demandTrend.title') }"
      :state="tableState"
      :sort="sort"
      :client-sort="false"
      :skeleton-rows="DEMAND_TREND_PAGE_SIZE"
      @update:sort="onSort"
    >
      <template #cell-product_name="{ row }">
        <span class="demand-trend__name inline-flex min-w-0 items-center gap-xs">
          <span
            class="demand-trend__chip inline-flex w-6 h-6 shrink-0 items-center justify-center rounded-full text-surface"
            :class="
              row.relative_demand_change === 'RISER'
                ? 'demand-trend__chip--riser'
                : 'demand-trend__chip--sinker'
            "
          >
            <Icon
              :name="row.relative_demand_change === 'RISER' ? 'chevron-up' : 'chevron-down'"
              :size="12"
            />
          </span>
          <Tooltip :text="String(row.product_name ?? '')" only-on-overflow>
            <span
              class="demand-trend__name-text overflow-hidden max-w-64 text-ellipsis whitespace-nowrap"
              >{{ row.product_name }}</span
            >
          </Tooltip>
        </span>
      </template>
      <template #cell-product_rank="{ row }">{{ rankLabel(row) }}</template>
      <template #empty>
        <span>{{ t('commerceAnalytics.productInsights.demandTrend.empty') }}</span>
      </template>
      <template #error>
        <span class="demand-trend__error inline-flex items-center gap-sm">
          <span>{{ t('commerceAnalytics.productInsights.demandTrend.error') }}</span>
          <Button variant="outline" size="sm" @click="trend.reload">
            {{ t('commerceAnalytics.productInsights.demandTrend.retry') }}
          </Button>
        </span>
      </template>
    </DataTable>

    <Pagination
      v-if="trend.totalRows.value > 0"
      :total="trend.totalRows.value"
      :rows="DEMAND_TREND_PAGE_SIZE"
      :page-sizes="[DEMAND_TREND_PAGE_SIZE]"
      :first="(trend.page.value - 1) * DEMAND_TREND_PAGE_SIZE"
      @change="onPage"
    />
  </div>
</template>

<style scoped>
.demand-trend .demand-trend__chip--riser {
  background-color: var(--ui-color-success);
}
.demand-trend .demand-trend__chip--sinker {
  background-color: var(--ui-color-danger);
}
</style>
